import { Component, EventEmitter, OnDestroy, OnInit, Output, inject, DestroyRef, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UntypedFormGroup, Validators, UntypedFormBuilder } from '@angular/forms';
import { Subscription } from 'rxjs';
import { GtwElementsBaseUrlsModel } from '../../../shared/_models/gtw-elements-base-urls.model';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { PostingPartnersService } from './posting-partners.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  firstGridRowObjectFromPayload,
  generalConfigFromGlobal,
  resolveFilterParams,
  resolveRowPayload,
} from '../../../shared/workspace-element-context';
import { AdminScreenContextService } from '../../shared/admin-screen-context.service';

@Component({
    selector: 'gtw-posting-partners',
    templateUrl: './posting-partners.component.html',
    styleUrls: ['./posting-partners.component.scss'],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class PostingPartnersComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);


  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  private readonly subs = new Subscription();

  rowData: any = {};
  baseURLs: GtwElementsBaseUrlsModel = { admin: '', api: '', custom: '', efile: '', pdfEngine: '', bulkPdfEngine: '' };
  baseGlobalParams: any = {};
  filterParams: any = {};
  USER_SETTINGS: any = {};

  ssoId: string = '';
  scenarioCode: string = '';
  isIssueKeyEnabled: string = '';
  issueKey: number = 0;

  totalTransactions: any;
  totalLoading: boolean = true;

  form!: UntypedFormGroup;
  formData: any;
  loader: boolean = false;
  minimise: boolean = false;

  constructor(
    private fb: UntypedFormBuilder,
    private gtwSnackbarService: GtwSnackbarService,
    private postingPartnersService: PostingPartnersService,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private adminScreenContext: AdminScreenContextService,
  ) {
    this.form = this.fb.group({
      PSHIP_PTR_POST_KEY: ['', Validators.required],
      COMBINATION_KEY: ['', Validators.required],
      PTR_COMBINATION_KEY: ['', Validators.maxLength(22)],
      IS_CURRENT: ['', Validators.maxLength(250)],
      TAX_YEAR: ['', Validators.required]
    });
  }

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    }));
    this.init();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);

    this.baseURLs = generalConfigFromGlobal(this.gtwGlobal);
    this.baseGlobalParams = header.globalParams || {};
    this.USER_SETTINGS = header.userSettingsData || {};
    this.filterParams = resolveFilterParams(this.adminScreenContext, this.gtwWorkspace);
    this.rowData = firstGridRowObjectFromPayload(resolveRowPayload(this.adminScreenContext, this.gtwWorkspace));

    this.ssoId = this.USER_SETTINGS?.user?.sso_id || '';
    this.scenarioCode = this.baseGlobalParams?.scenarioCode || '';
    this.isIssueKeyEnabled = this.filterParams?.is_issue_key_enabled
      || this.baseGlobalParams?.is_issue_key_enabled
      || 'N';
    this.issueKey = Number(this.baseGlobalParams?.issue_key || this.filterParams?.issue_key || 0);
  }

  init(): void {
    this.populateScreen();
    this.loadTransactionCount();
  }

  populateScreen(): void {
    if (!this.rowData || Object.keys(this.rowData).length === 0) {
      return;
    }
    this.form.patchValue({
      PSHIP_PTR_POST_KEY: this.rowData.PSHIP_PTR_POSTING_KEY,
      COMBINATION_KEY: this.rowData.COMBINATION_KEY,
      PTR_COMBINATION_KEY: String(this.rowData.PTR_COMBINATION_KEY),
      IS_CURRENT: this.rowData.IS_CURRENT,
      TAX_YEAR: this.rowData.TAX_YEAR
    });
  }

  private loadTransactionCount(): void {
    if (!this.rowData || Object.keys(this.rowData).length === 0) {
      return;
    }
    if (!this.filterParams?.scenario || !this.baseURLs?.api || !this.baseGlobalParams?.jcd_key) {
      return;
    }
    this.totalLoading = true;
    const params = {
      tax_year: this.rowData.TAX_YEAR,
      scenario: this.filterParams?.scenario,
      jcd_key: this.baseGlobalParams?.jcd_key,
      sso_id: this.ssoId,
      pship_cc_key: this.rowData.COMBINATION_KEY,
      ptr_cc_key: this.rowData.PTR_COMBINATION_KEY
    };
    this.postingPartnersService.getTransactionCount(this.baseURLs.api, params).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data: any) => {
      if (data !== 'error') {
        if (data.jsonObject[0].hasOwnProperty("COUNT(DISTINCTTRANS_HEADER_KEY)")) {
          this.totalTransactions = data.jsonObject[0]["COUNT(DISTINCTTRANS_HEADER_KEY)"];
        } else {
          this.totalTransactions = 0;
        }
        this.totalLoading = false;
      } else {
        console.log("Error while fetching data");
        this.totalTransactions = 0;
        this.totalLoading = false;
      }
    });
  }

  save(): void {
    if (this.form.status === 'INVALID') {
      this.gtwSnackbarService.openSnackBar("Please check form details and try again", 'gtw-snackbar--red');
      return;
    }
    const formValues = this.form.value;
    delete formValues.tax_year;

    const clob_data = [{ ...formValues }];
    const params: saveParams = {
      tax_year: this.rowData.TAX_YEAR,
      scenario: this.filterParams?.scenario,
      jcd_key: this.baseGlobalParams?.jcd_key
    };
    const clob_settings: saveParams = { tax_year: params.tax_year, scenario: params.scenario, jcd_key: Number(params.jcd_key) };
    this.postingPartnersService.saveLineChanges(this.baseURLs.api, params, clob_settings, clob_data, this.ssoId, this.isIssueKeyEnabled, this.issueKey, this.scenarioCode).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data: any) => {
      if (data !== 'error') {
        this.requestGridUpdate.emit('refresh');
        this.loader = false;
      } else {
        console.log("Error while saving");
        this.loader = false;
      }
    }, () => {
      console.log("Error while saving");
      this.loader = false;
    });
  }

  cancel(): void {
    this.requestGridUpdate.emit('cancel');
  }
}

export type saveParams = {
  tax_year: string | undefined;
  scenario: string | undefined;
  jcd_key: any;
}
